import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { apiErrorMessage } from "../services/api";
import { formatDateTime } from "../services/datetime";
import { fetchPayment } from "../services/paymentService";
import { labelFor } from "../services/enumLabels";

/**
 * "View payment(s)" on a booking: the receipt of each payment taken for it.
 *
 * `payments` is the appointment's own short list (detail endpoint); the full
 * receipt — breakdown, refunds, who took it — is fetched for the one shown.
 * Every amount comes from the API as a string: nothing is added up here.
 */
export default function PaymentReceiptModal({ open, payments = [], currency, onClose }) {
  const { t, i18n } = useTranslation();
  const [selectedId, setSelectedId] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // The latest payment first, whenever the dialog opens.
  useEffect(() => {
    if (open) setSelectedId(payments[payments.length - 1]?.id ?? null);
  }, [open, payments]);

  useEffect(() => {
    if (!open || !selectedId) return undefined;
    let live = true;
    setLoading(true);
    setError("");
    fetchPayment(selectedId)
      .then(({ payment }) => live && setReceipt(payment))
      .catch((err) => live && setError(apiErrorMessage(err, t("receipt.loadFailed"))))
      .finally(() => live && setLoading(false));

    return () => {
      live = false;
    };
  }, [open, selectedId, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const code = receipt?.currency?.code ?? currency?.code ?? "";
  // Amounts are LTR runs inside an RTL sentence: keep "12.000 JOD" in order.
  const money = (value) => (
    <bdi dir="ltr" className="tabular-nums">
      {value ?? "0.00"} {code}
    </bdi>
  );
  const row = (label, value, key) => (
    <div key={key} className="flex items-baseline justify-between gap-4 py-1">
      <dt className="text-ink-2">{label}</dt>
      <dd className="text-end font-medium text-ink">{value}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="receipt-title"
        className="card max-h-[90vh] w-full max-w-md overflow-y-auto p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 id="receipt-title" className="text-lg font-bold">
            {t(payments.length > 1 ? "receipt.titleMany" : "receipt.title")}
          </h2>
          <button type="button" className="btn-secondary" onClick={onClose} aria-label={t("common.close")}>
            ×
          </button>
        </div>

        {payments.length > 1 && (
          <div role="tablist" aria-label={t("receipt.titleMany")} className="mb-3 flex flex-wrap gap-1.5">
            {payments.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={p.id === selectedId}
                onClick={() => setSelectedId(p.id)}
                className={`rounded-full border px-3 py-1 text-caption font-medium ${
                  p.id === selectedId
                    ? "border-accent bg-accent-wash text-accent-text"
                    : "border-line bg-surface text-ink-2 hover:border-line-strong"
                }`}
              >
                <bdi dir="ltr">{p.reference}</bdi> · {money(p.amount)}
              </button>
            ))}
          </div>
        )}

        {error && <p className="field-error">{error}</p>}
        {loading && !receipt && <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>}

        {receipt && (
          <dl className="divide-y divide-line text-sm">
            <div className="pb-2">
              {row(t("transactions.table.reference"), <bdi dir="ltr">{receipt.reference}</bdi>)}
              {row(
                t("receipt.paidAt"),
                receipt.paid_at ? formatDateTime(receipt.paid_at, i18n.language) : "—"
              )}
              {row(t("transactions.table.method"), labelFor("payment_method", receipt.method))}
              {row(t("transactions.table.status"), labelFor("payment_status", receipt.status))}
              {receipt.received_by && row(t("receipt.receivedBy"), receipt.received_by)}
              {receipt.branch?.name && row(t("receipt.branch"), receipt.branch.name)}
            </div>
            <div className="py-2">
              {row(t("receipt.subtotal"), money(receipt.breakdown?.subtotal))}
              {Number(receipt.breakdown?.discount) > 0 && row(t("receipt.discount"), money(receipt.breakdown.discount))}
              {Number(receipt.loyalty_discount) > 0 && row(t("receipt.reward"), money(receipt.loyalty_discount))}
              {row(t("transactions.table.tax"), money(receipt.breakdown?.tax_amount))}
              {row(t("receipt.total"), money(receipt.amount))}
              {Number(receipt.amount_refunded) > 0 && row(t("transactions.refundedAmount"), money(receipt.amount_refunded))}
            </div>
            {(receipt.refunds ?? []).length > 0 && (
              <div className="pt-2">
                <p className="mb-1 font-semibold text-ink">{t("receipt.refunds")}</p>
                {receipt.refunds.map((refund) =>
                  row(
                    formatDateTime(refund.refunded_at ?? refund.created_at, i18n.language) || "—",
                    money(refund.amount),
                    refund.id
                  )
                )}
              </div>
            )}
          </dl>
        )}

        <div className="mt-4 flex justify-end">
          <Link to="/transactions" className="text-sm font-medium text-accent-text hover:underline">
            {t("receipt.openTransactions")}
          </Link>
        </div>
      </div>
    </div>
  );
}
